import { Flow } from "@repo/core/services/entities/Unico";
import {
  DocumentTypeEnum,
  RequiredDocumentTypeEnum
} from "@repo/core/enums/proposals";
import { RequiredDocuments } from "../../../store/preProposalStore";
import { MAX_RETRY_ATTEMPTS } from "../constants";

export interface ExistUnicoRecord {
  blnExisteRegistro: boolean;
}

/**
 * Checks if required documents include both Document ID front (0001) and back (0010)
 */
export const requiresDocumentFrontAndBack = (
  docs: RequiredDocuments[]
): boolean => {
  const types = docs.map((d) => String(d.strTipoDocumento).trim());
  return (
    types.includes(DocumentTypeEnum.DOCUMENT_FRONT) &&
    types.includes(DocumentTypeEnum.DOCUMENT_BACK)
  );
};

export const requiresIdentityDocumentCapture = (
  docs: RequiredDocuments[]
): boolean => {
  const types = docs.map((d) => String(d.strTipoDocumento).trim());
  return (
    types.includes(DocumentTypeEnum.DOCUMENT_FRONT) ||
    types.includes(DocumentTypeEnum.DOCUMENT_BACK)
  );
};

export const shouldRunUnicoProcessForRequiredDocuments = (
  docs?: RequiredDocuments[] | null
): boolean => {
  if (!docs?.length) {
    return false;
  }

  return docs.some(
    (doc) =>
      String(doc.strTipoDocumento).trim() === RequiredDocumentTypeEnum.BIOMETRY
  );
};

export const hasUploadedIdentityDocuments = (
  uploadedProposalDocumentTypes?: string[] | null
): boolean => {
  if (!uploadedProposalDocumentTypes?.length) {
    return false;
  }

  return (
    uploadedProposalDocumentTypes.includes(DocumentTypeEnum.DOCUMENT_FRONT) &&
    uploadedProposalDocumentTypes.includes(DocumentTypeEnum.DOCUMENT_BACK)
  );
};

export const determineUnicoFlow = (
  existUnicoRecord?: ExistUnicoRecord | null,
  requiredDocuments?: RequiredDocuments[] | null,
  uploadedProposalDocumentTypes?: string[] | null
): Flow => {
  // Business rule: when there is a prior Unico record, always use idtoken.
  if (existUnicoRecord?.blnExisteRegistro) {
    return Flow.idtoken;
  }

  // RN014: idunicodocs when identity docs are required AND not yet uploaded
  if (
    requiredDocuments &&
    requiresIdentityDocumentCapture(requiredDocuments) &&
    !hasUploadedIdentityDocuments(uploadedProposalDocumentTypes)
  ) {
    return Flow.iddocs;
  }

  // RN013: idcheck for biometry-only (or when identity docs are already uploaded)
  return Flow.idcheck;
};

export const getCachedPositiveExistUnicoRecord = (
  blnExisteRegistroUnico?: boolean
): ExistUnicoRecord | null => {
  return blnExisteRegistroUnico === true ? { blnExisteRegistro: true } : null;
};

export const shouldWaitForExistUnicoRecordValidation = (
  hasExplicitBiometryReuseProcessId: boolean
): boolean => {
  return !hasExplicitBiometryReuseProcessId;
};

export const getEffectiveExistUnicoRecordForFlow = (
  existUnicoRecord: ExistUnicoRecord | null | undefined,
  shouldForceNewBiometry: boolean
): ExistUnicoRecord | null | undefined => {
  if (existUnicoRecord?.blnExisteRegistro) {
    return existUnicoRecord;
  }

  return shouldForceNewBiometry
    ? { blnExisteRegistro: false }
    : existUnicoRecord;
};

export const shouldApplyForcedBiometryFlowOverride = (
  flow: Flow,
  shouldForceNewBiometry: boolean
): boolean => {
  return shouldForceNewBiometry && flow !== Flow.idtoken;
};

export const shouldRetryProcess = (retryCount: number): boolean => {
  return retryCount < MAX_RETRY_ATTEMPTS;
};
